// @ts-nocheck
import React, { useMemo } from 'react';
import cls from '../StatisticsPage.module.scss';
import { useTranslation } from 'react-i18next';

// import zoomPlugin from 'chartjs-plugin-zoom';
import { Chart } from 'react-chartjs-2';

import { Chart as ChartJS } from 'chart.js';
import { registerables } from 'chart.js';

import {
  formatDataForChartTolltip,
  formatDateForChart,
} from 'src/utils/helpers/timeHelper';
import PageDataLoader from 'src/components/UI/Loaders/PageDataLoader/PageDataLoader';
import trimNumber from 'src/utils/helpers/trimNumber';
import useMobileSize from 'src/hooks/useMobileSize';
import useStatusesTranslate from 'src/hooks/translate/useStatusesTranslate';
import BarsChartSkeleton from '../../../UI/skeletons/BarsChartSkeleton';

ChartJS.register(
  ...registerables
  // zoomPlugin
);

interface IProps {
  statistic: any;
  statisticLoading: boolean;
  isDayChart: boolean;
  shouldShowAllLine?: boolean;
  chartClassName?: string;
  chartBoxClassName?: string;
  legendClassName?: string;
  isSkeletonLoader?: boolean;
}
const getOrCreateLegendList = (chart, id) => {
  const legendContainer = document.getElementById(id);
  let listContainer = legendContainer.querySelector('ul');

  if (!listContainer) {
    listContainer = document.createElement('ul');
    listContainer.classList.add(cls['labels']);
    legendContainer.appendChild(listContainer);
  }

  return listContainer;
};

const htmlLegendPlugin = {
  id: 'htmlLegend',
  afterUpdate(chart, args, options) {
    const ul = getOrCreateLegendList(chart, options.containerID);

    // Remove old legend items
    while (ul.firstChild) {
      ul.firstChild.remove();
    }

    // Reuse the built-in legendItems generator
    const items = chart.options.plugins.legend.labels.generateLabels(chart);
    items.forEach((item) => {
      const li = document.createElement('li');
      li.classList.add(cls['label']);
      li.onclick = () => {
        const { type } = chart.config;
        if (type === 'pie' || type === 'doughnut') {
          // Pie and doughnut charts only have a single dataset and visibility is per item
          chart.toggleDataVisibility(item.index);
        } else {
          chart.setDatasetVisibility(
            item.datasetIndex,
            !chart.isDatasetVisible(item.datasetIndex)
          );
        }
        chart.update();
      };

      // Color box
      const boxSpan = document.createElement('span');
      boxSpan.style.backgroundColor = item.fillStyle;
      boxSpan.style.borderColor = item.strokeStyle;
      boxSpan.style.width = '20px';
      boxSpan.style.height = '20px';
      boxSpan.style.borderRadius = '50%';
      boxSpan.style.border = 'none';

      // Text
      const textContainer = document.createElement('p');
      textContainer.style.textDecoration = item.hidden ? 'line-through' : '';

      const text = document.createTextNode(item.text);
      textContainer.appendChild(text);

      li.appendChild(boxSpan);
      li.appendChild(textContainer);
      ul.appendChild(li);
    });
  },
};
const ChartBlock = ({
  statistic,
  statisticLoading,
  isDayChart,
  shouldShowAllLine = true,
  chartClassName = '',
  isSkeletonLoader = false,
  chartBoxClassName = '',
  legendClassName = '',
}: IProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'statistics' });
  const { t: tg } = useTranslation('translation');

  const statusesTypesMap = useStatusesTranslate();

  // const [isZoomed, setIsZoomed] = useState<any>(null)

  // const resetZoom = () => {
  //   if(isZoomed){
  //     isZoomed.resetZoom();
  //     setIsZoomed(null);
  //   }
  // }
  const chartData = useMemo(() => {
    if (!statistic || !statistic?.dates) return null;
    const labels: any[] = [];
    const datasets: any[] = [];
    // let isDayChart = false
    // const dataDateKeys = Object.keys(statistic.dates);
    // if(dataDateKeys.length===24 && dataDateKeys?.at(0)?.split(" ")[0] === dataDateKeys?.at(-1)?.split(" ")[0] ){
    //   isDayChart = true
    // }
    Object.entries(statistic.statuses).map(
      ([statusId, statusValue]: [any, any]) => {
        const data: any = [];
        Object.entries(statistic.dates).map(
          ([dateId, dateValue]: [any, any]) => {
            const statusesDataForDate = dateValue.statuses;
            if (statusesDataForDate[statusId]) {
              data.push(statusesDataForDate[statusId].count);
            } else {
              data.push(0);
            }
          }
        );
        datasets.push({
          type: 'bar' as const,
          label: `${trimNumber(statusValue.percentage)}% (${statusValue.count}) ${statusesTypesMap[statusValue.name] || statusValue.name?.replaceAll('_', ' ')}`,
          data: data,
          backgroundColor: statusValue.color.includes('#')
            ? statusValue.color
            : `#${statusValue.color}`,
          // borderColor: "#808080",
          // borderWidth: 1,
          stack: 'Stack 0',
          zIndexGroup: 'bars',
          borderColor: 'rgba(0, 0, 0, 0)',
          borderWidth: isDayChart ? 3 : 1,
          borderSkipped: false,

          order: 2,
          metadata: {
            status: statusValue,
            statusName: statusesTypesMap[statusValue.name] || statusValue.name,
          },
        });
      }
    );
    datasets.sort((a, b) => {
      return b.metadata.status.count - a.metadata.status.count;
    });
    const resultDataForDatasets: any[] = [];
    Object.entries(statistic.dates).map(([dateId, dateValue]: [any, any]) => {
      labels.push(formatDateForChart(dateId, isDayChart));
      resultDataForDatasets.push(dateValue.total_count);
    });
    if (shouldShowAllLine && Object.entries(statistic.dates).length > 1) {
      datasets.unshift({
        type: 'line' as const,
        label: `100% (${Object.values(statistic.statuses).reduce(
          (acc: number, current: any) => {
            return acc + +current.count;
          },
          0
        )}) ${tg('select-all')}`,
        data: resultDataForDatasets,
        borderColor: 'black',
        borderWidth: 2,
        backgroundColor: '#000',
        pointRadius: 4,
        fill: false,
        order: 1,
      });
    }
    const res = {
      labels,
      datasets: datasets,
      metadata: {
        dates: Object.keys(statistic.dates).map((metadataDate) =>
          formatDataForChartTolltip(metadataDate)
        ),
      },
    };
    return res;
  }, [statistic]);
  const isMobile = useMobileSize(['<=', '500px']);

  const options = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      outerWidth: '100%',

      scales: {
        x: {
          stacked: true,
          grid: {
            drawOnChartArea: false,
            drawTicks: false,
          },
          zoom: {
            enabled: false,
          },
        },
        y: {
          stacked: true,
          grid: {
            drawOnChartArea: false,
            drawTicks: false,
          },
          offset: true,
          beginAtZero: true,
          ticks: {
            min: 0,
            // max: 12,
            stepSize: 1,
          },
        },
      },
      plugins: {
        tooltip: {
          enabled: false,
          external: function (context) {
            const tooltip = context.tooltip;
            if (!tooltip.dataPoints) {
              return;
            }
            const dataIndex = tooltip.dataPoints[0].dataIndex;
            const barData = [];
            chartData?.datasets.forEach((dataset) => {
              if (dataset.type === 'bar' && dataset.data[dataIndex] > 0) {
                barData.push({
                  metadata: dataset.metadata,
                  data: dataset.data[dataIndex],
                });
              }
            });

            let tooltipEl = document.getElementById('chartjs-tooltip');

            if (!tooltipEl) {
              tooltipEl = document.createElement('div');
              tooltipEl.id = 'chartjs-tooltip';
              tooltipEl.innerHTML = '<table></table>';
              document.body.querySelector('.chart-box').appendChild(tooltipEl);
            }

            const tooltipModel = context.tooltip;
            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = 0;
              return;
            }

            tooltipEl.classList.remove('above', 'below', 'no-transform');
            if (tooltipModel.yAlign) {
              tooltipEl.classList.add(tooltipModel.yAlign);
            } else {
              tooltipEl.classList.add('no-transform');
            }

            function getBody(bodyItem) {
              return bodyItem.lines;
            }

            // Set Text
            if (tooltipModel.body) {
              let barHtml = '';
              barData.forEach((bar) => {
                barHtml =
                  barHtml +
                  `<div class="chart-tooltip-custom__item">
                    <div class="chart-tooltip-custom__color" style="background-color: ${bar.metadata.status.color || '#fff'};${(!bar.metadata.status.color || bar.metadata.status.color === '#fff') && 'border: 1px solid black'}"></div>
                    <span class="chart-tooltip-custom__count">(${bar.data}) </span>
                    ${bar.metadata.statusName?.replaceAll('_', ' ')}
                  </div>`;
              });

              const innerHtml = `<div class="chart-tooltip-custom__wrapper">
                  <div class="chart-tooltip-custom__date">${chartData?.metadata.dates[dataIndex]}</div>
                  <div>
                    ${barHtml}
                  </div>
                </div>`;
              const tableRoot = tooltipEl.querySelector('table');
              tableRoot.innerHTML = innerHtml;
            }

            tooltipEl.style.opacity = 1;
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.left =
              window.pageXOffset +
              tooltipModel.caretX -
              (isMobile ? 50 : 0) +
              'px';
            tooltipEl.style.top =
              window.pageYOffset + tooltipModel.caretY + 'px';
            tooltipEl.style.padding =
              tooltipModel.padding + 'px ' + tooltipModel.padding + 'px';
            tooltipEl.style.pointerEvents = 'none';
          },
        },
        htmlLegend: {
          // ID of the container to put the legend in
          containerID: 'legend-container',
        },
        legend: {
          display: false,
        },
        // legend:{
        //   position: (isMobile ? 'top' :'right') as const,
        //   align: isMobile ? 'center' : 'start' as const,
        //   // onHover: (event:any, legendItem:any, legend:any) => {
        //   //   const datasetIndex = legendItem.datasetIndex;
        //   //   const chart = legend.chart;
        //   //   const meta = chart.getDatasetMeta(datasetIndex);
        //
        //   //   // Затухание других стеков при наведении на элемент легенды
        //   //   chart.data.datasets.forEach((dataset:any, index:any) => {
        //   //     const otherMeta = chart.getDatasetMeta(index);
        //
        //   //     if (index !== datasetIndex) {
        //   //       otherMeta.data.forEach((bar:any) => {
        //   //         bar.options.hidden = true;
        //   //       });
        //   //     }
        //   //   });
        //
        //   //   // Подсветить выбранный стек
        //   //   meta.data.forEach((bar:any) => {
        //   //     console.log("bar", bar);
        //
        //   //     bar.options.backgroundColor = "red";
        //   //     bar.backgroundColor = "red";
        //   //   });
        //
        //   //   // chart.update();
        //   // },
        //   // onLeave: (event:any, legendItem:any, legend:any) => {
        //   //   const chart = legend.chart;
        //
        //   //   // Вернуть нормальное состояние при уходе с элемента легенды
        //   //   chart.data.datasets.forEach((dataset:any, index:any) => {
        //   //     const otherMeta = chart.getDatasetMeta(index);
        //
        //   //     otherMeta.data.forEach((bar:any) => {
        //   //       bar.options.hidden = false;
        //   //     });
        //   //   });
        //
        //   //   chart.update();
        //   // },
        // },
        // zoom: {
        //   onTouch: function ({ chart }: { chart:any }) {
        //     const xAxis = chart.scales['x'];
        //     console.log("xAxis", xAxis);

        //     const chartArea = chart.chartArea;
        //     const mouseX = chartArea.left + (chart._active[0] ? chart._active[0].x : 0);

        //     // Проверяем, находится ли курсор в области меток оси X
        //     if (mouseX >= xAxis.left && mouseX <= xAxis.right) {
        //       return false; // Отменяем событие зума
        //     }

        //     return true;
        //   },
        //   zoom: {
        //     wheel: {
        //       enabled: true,
        //     },
        //     pinch: {
        //       enabled: true
        //     },
        //     drag:{
        //       enabled: true
        //     },
        //     mode: 'x' as const,
        //     onZoom: function ({ chart }:{chart:any}) {
        //       // Этот код будет выполнен при каждом изменении масштаба (зуме)
        //       console.log('График был зумирован!', chart.getZoomLevel());
        //       if(chart.getZoomLevel()>1){
        //         setIsZoomed(chart)
        //       }else{
        //         setIsZoomed(null)
        //       }

        //       // const scales = chart.scales;
        //       // const xAxis = scales['x'];

        //       // // Проверяем, что зум произошел внутри области меток оси X
        //       // if (xAxis.isHorizontal() && xAxis.options.ticks.max > xAxis.max && xAxis.options.ticks.min < xAxis.min) {
        //       //   console.log('Zoom within X-axis labels area');
        //       // } else {
        //       //   // Если зум произошел в области меток, отменяем его
        //       //   chart.resetZoom();
        //       // }
        //       // return true;
        //     },

        //   },
        // },
      },
    };
  }, [chartData]);

  if (statisticLoading) {
    return isSkeletonLoader ? (
      <BarsChartSkeleton width="100%" height="100%" />
    ) : (
      <PageDataLoader />
    );
  }

  return (
    <>
      <div className={`${cls['chart']} ${chartClassName}`}>
        <div className={`${cls['chart-box']} chart-box ${chartBoxClassName}`}>
          {chartData && chartData.datasets.length ? (
            <Chart
              type="bar"
              options={options}
              data={chartData}
              plugins={[htmlLegendPlugin]}
            />
          ) : (
            <div className={`${cls['chart-no-data']}`}>
              {t('chart-no-data')}
            </div>
          )}
        </div>
        <div
          className={`${cls['labels-container']} ${legendClassName}`}
          id="legend-container"
        ></div>

        <div className={`${cls['chart-data']}`}>
          {/* {isZoomed && 
                <Button theme={ButtonTheme.PRIMARY} onClick={resetZoom} className="mt-5">
                  <span>{t('button-zoom')}</span>
                </Button>
              } */}
        </div>
      </div>
    </>
  );
};

export default ChartBlock;

// Нет значения
// Добавлен: created_at
// Закреплен: pinned_at
// Отправлен: sand_datetime
// Изменен : updated_at
// Cдан : finished_at
// Дата доставки: delivered_at

// manager_id - user list
